import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Plus } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useCreateTask, CreateTaskInput } from "@/api/tasks";
import { useDepots } from "@/api/depots";
import { useOrders } from "@/api/orders";
import { Checkbox } from "@/components/ui/checkbox";

// Form validation schema with Zod
const formSchema = z.object({
  type: z.enum([
    "PICKUP",
    "DELIVERY",
    "DEPOT_RECEIVE",
    "DEPOT_SORT",
    "DEPOT_DISPATCH",
    "RETURN",
  ]),
  status: z
    .enum(["PENDING", "IN_PROGRESS", "COMPLETED", "FAILED", "CANCELLED"])
    .optional(),
  orderId: z.string().min(1, "Order is required"),
  depotId: z.string().min(1, "Depot is required"),
  parcelIds: z.array(z.string()).min(1, "At least one parcel must be selected"),
  description: z.string().optional(),
});

type FormValues = z.infer<typeof formSchema>;

interface CreateTaskDialogProps {
  onCreated?: () => void;
  initialOrderId?: string | null;
}

export function CreateTaskDialog({
  onCreated,
  initialOrderId,
}: CreateTaskDialogProps) {
  // Only manage internal dialog state when not embedded in another dialog
  const [open, setOpen] = useState(false);
  const { toast } = useToast();

  // Fetch depots for dropdown
  const { data: depots } = useDepots();

  // Fetch orders for dropdown
  const { data: ordersResponse } = useOrders(1, 50);

  // Setup form with react-hook-form and zod validation
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      type: "PICKUP",
      status: "PENDING",
      orderId: initialOrderId ?? "",
      depotId: "",
      parcelIds: [],
      description: "",
    },
  });

  // Effect to update form when initialOrderId changes
  useEffect(() => {
    if (initialOrderId) {
      form.setValue("orderId", initialOrderId);
    }
  }, [initialOrderId, form]);

  // Handle dialog closing
  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
    if (!newOpen) {
      form.reset({
        type: "PICKUP",
        status: "PENDING",
        orderId: "",
        depotId: "",
        parcelIds: [],
        description: "",
      });
    }
  };

  // Get selected order to populate parcels
  const selectedOrderId = form.watch("orderId");
  const selectedOrder = ordersResponse?.data.find(
    (order) => order.id === selectedOrderId
  );

  // Create task mutation
  const createTaskMutation = useCreateTask({
    onSuccess: () => {
      toast({
        title: "Tasks created",
        description: "Tasks were created successfully.",
      });
      if (onCreated) onCreated();
      form.reset();
      setOpen(false);
    },
    onError: (error) => {
      toast({
        title: "Error creating tasks",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Handle creating multiple tasks
  const createTasks = async (parcelIds: string[], values: FormValues) => {
    for (const parcelId of parcelIds) {
      const taskInput: CreateTaskInput = {
        type: values.type,
        status: values.status,
        orderId: values.orderId,
        depotId: values.depotId,
        parcelId: parcelId,
        description: values.description,
      };

      try {
        // Use mutateAsync to ensure tasks are created sequentially
        await createTaskMutation.mutateAsync(taskInput);
      } catch (error) {
        // Error will be handled by the mutation's onError callback
        console.error("Error creating task:", error);
        break;
      }
    }
  };

  // Form submission handler
  const onSubmit = (values: FormValues) => {
    if (values.parcelIds.length === 0) {
      toast({
        title: "Please select at least one parcel",
        variant: "destructive",
      });
      return;
    }

    void createTasks(values.parcelIds, values);
  };

  // Render the form content
  const renderFormContent = () => (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          {/* Task Type */}
          <FormField
            control={form.control}
            name="type"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Task Type</FormLabel>
                <Select value={field.value} onValueChange={field.onChange}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select task type" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="PICKUP">Pickup</SelectItem>
                    <SelectItem value="DELIVERY">Delivery</SelectItem>
                    <SelectItem value="DEPOT_RECEIVE">Depot Receive</SelectItem>
                    <SelectItem value="DEPOT_SORT">Depot Sort</SelectItem>
                    <SelectItem value="DEPOT_DISPATCH">
                      Depot Dispatch
                    </SelectItem>
                    <SelectItem value="RETURN">Return</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Task Status */}
          <FormField
            control={form.control}
            name="status"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Status</FormLabel>
                <Select
                  value={field.value ?? ""}
                  onValueChange={field.onChange}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select status" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="PENDING">Pending</SelectItem>
                    <SelectItem value="IN_PROGRESS">In Progress</SelectItem>
                    <SelectItem value="COMPLETED">Completed</SelectItem>
                    <SelectItem value="FAILED">Failed</SelectItem>
                    <SelectItem value="CANCELLED">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          {/* Order Selection */}
          <FormField
            control={form.control}
            name="orderId"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Order</FormLabel>
                <Select
                  value={field.value}
                  onValueChange={(value) => {
                    field.onChange(value);
                    // Clear parcelIds when order changes
                    form.setValue("parcelIds", []);
                  }}
                  disabled={!!initialOrderId}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select order" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {ordersResponse?.data.map((order) => (
                      <SelectItem key={order.id} value={order.id}>
                        #{order.orderNumber} - {order.senderName}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Depot Selection */}
          <FormField
            control={form.control}
            name="depotId"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Depot</FormLabel>
                <Select value={field.value} onValueChange={field.onChange}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select depot" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {depots?.map((depot) => (
                      <SelectItem key={depot.id} value={depot.id}>
                        {depot.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {/* Parcel Selection with Checkboxes - Only show if order is selected */}
        <FormField
          control={form.control}
          name="parcelIds"
          render={() => (
            <FormItem>
              <div className="mb-4">
                <FormLabel>Parcels</FormLabel>
                <FormMessage />
              </div>
              <div className="flex flex-col space-y-2 border rounded-md p-4 max-h-[200px] overflow-y-auto">
                {!selectedOrderId && (
                  <div className="text-sm text-muted-foreground">
                    Select an order to view parcels
                  </div>
                )}
                {selectedOrderId && selectedOrder?.parcels.length === 0 && (
                  <div className="text-sm text-muted-foreground">
                    No parcels found for this order
                  </div>
                )}
                {selectedOrderId &&
                  selectedOrder?.parcels.map((parcel) => (
                    <FormField
                      key={parcel.id}
                      control={form.control}
                      name="parcelIds"
                      render={({ field }) => {
                        return (
                          <FormItem
                            key={parcel.id}
                            className="flex flex-row items-start space-x-3 space-y-0"
                          >
                            <FormControl>
                              <Checkbox
                                checked={field.value?.includes(parcel.id)}
                                onCheckedChange={(checked) => {
                                  const updatedValues = checked
                                    ? [...field.value, parcel.id]
                                    : field.value?.filter(
                                        (value) => value !== parcel.id
                                      );
                                  field.onChange(updatedValues);
                                }}
                              />
                            </FormControl>
                            <FormLabel className="font-normal cursor-pointer text-sm">
                              {parcel.trackingNumber} - {parcel.weight}kg
                              {parcel.dimensions &&
                                ` (${parcel.dimensions.length}x${parcel.dimensions.width}x${parcel.dimensions.height}cm)`}
                            </FormLabel>
                          </FormItem>
                        );
                      }}
                    />
                  ))}
              </div>
            </FormItem>
          )}
        />

        {/* Description */}
        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Description (Optional)</FormLabel>
              <FormControl>
                <Textarea placeholder="Enter task description..." {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              if (initialOrderId) {
                // When embedded in another dialog, notify parent
                if (onCreated) onCreated();
              } else {
                // When standalone, close the dialog
                setOpen(false);
              }
            }}
          >
            Cancel
          </Button>
          <Button type="submit" disabled={createTaskMutation.isPending}>
            {createTaskMutation.isPending ? "Creating..." : "Create Tasks"}
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );

  // If this is being used as an embedded form (with initialOrderId)
  if (initialOrderId) {
    return renderFormContent();
  }

  // Otherwise, render with our own dialog wrapper
  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      {!initialOrderId && (
        <DialogTrigger asChild>
          <Button>
            <Plus className="mr-2 h-4 w-4" />
            Create Task
          </Button>
        </DialogTrigger>
      )}
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Create New Tasks</DialogTitle>
        </DialogHeader>
        {renderFormContent()}
      </DialogContent>
    </Dialog>
  );
}
